import { AlertCircle, Copy, Gift, Loader2, Share2, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ReferralEvents, trackReferralEvent } from "@/utils/analytics";
import { isReferralSystemEnabled } from "@/utils/featureFlags";
import {
  createLinkedInShareText,
  createReferralLink,
  maskEmail,
} from "@/utils/referralValidation";

interface UserCredits {
  total_credits: number;
  used_credits: number;
  pending_credits: number;
  available_discount: number;
}

interface ReferralUse {
  id: string;
  used_at: string;
  report_completed: boolean;
  credit_awarded: boolean;
  referred_user_id: string;
}

export default function ReferralSection() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [isEnabled, setIsEnabled] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [referralCodeInput, setReferralCodeInput] = useState("");
  const [submittingReferral, setSubmittingReferral] = useState(false);
  const [credits, setCredits] = useState<UserCredits | null>(null);
  const [referralHistory, setReferralHistory] = useState<ReferralUse[]>([]);
  const [canEnterReferralCode, setCanEnterReferralCode] = useState(false);

  useEffect(() => {
    checkFeatureFlag();
  }, [user]);

  const checkFeatureFlag = async () => {
    if (!user) return;

    const enabled = await isReferralSystemEnabled();
    setIsEnabled(enabled);

    if (enabled) {
      await Promise.all([
        fetchReferralCode(),
        fetchCredits(),
        fetchReferralHistory(),
        checkReferralEligibility(),
      ]);
    }

    setLoading(false);
  };

  const fetchReferralCode = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("referral_codes")
        .select("code")
        .eq("user_id", user.id)
        .single();

      if (error && error.code !== "PGRST116") {
        throw error;
      }

      if (data) {
        setReferralCode(data.code);
      }
    } catch (error) {
      console.error("Error fetching referral code:", error);
    }
  };

  const fetchCredits = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase.rpc("get_user_credits", {
        user_uuid: user.id,
      });

      if (error) throw error;

      if (data && data.length > 0) {
        setCredits(data[0]);
      }
    } catch (error) {
      console.error("Error fetching credits:", error);
    }
  };

  const fetchReferralHistory = async () => {
    if (!user) return;

    try {
      // First get the referral code IDs for this user
      const { data: referralCodes, error: codesError } = await supabase
        .from("referral_codes")
        .select("id")
        .eq("user_id", user.id);

      if (codesError) throw codesError;

      const referralCodeIds = referralCodes?.map((code) => code.id) || [];

      const { data, error } = await supabase
        .from("referral_uses")
        .select(
          "id, used_at, report_completed, credit_awarded, referred_user_id"
        )
        .in("referral_code_id", referralCodeIds);

      if (error) throw error;
      setReferralHistory(data || []);
    } catch (error) {
      console.error("Error fetching referral history:", error);
    }
  };

  const checkReferralEligibility = async () => {
    if (!user) return;

    // Check if user was created within last 24 hours
    const userCreatedAt = new Date(user.created_at);
    const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const isEligible = userCreatedAt > twentyFourHoursAgo;

    // Check if user already used a referral code
    const { data: existingUse } = await supabase
      .from("referral_uses")
      .select("id")
      .eq("referred_user_id", user.id)
      .single();

    setCanEnterReferralCode(isEligible && !existingUse);
  };

  const generateReferralCode = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase.functions.invoke(
        "api/generate-referral-code"
      );

      if (error) throw error;

      setReferralCode(data.referralCode);

      trackReferralEvent({
        event: ReferralEvents.CODE_GENERATED,
        userId: user.id,
        referralCode: data.referralCode,
      });

      toast({
        title: "Succes",
        description: "Codul de recomandare a fost generat cu succes!",
      });
    } catch (error) {
      console.error("Error generating referral code:", error);
      toast({
        title: "Eroare",
        description: "Nu s-a putut genera codul de recomandare",
        variant: "destructive",
      });
    }
  };

  const submitReferralCode = async () => {
    if (!user || !referralCodeInput.trim()) return;

    setSubmittingReferral(true);

    try {
      const { data, error } = await supabase.functions.invoke(
        "api/validate-referral",
        {
          body: {
            referralCode: referralCodeInput.trim(),
            userEmail: user.email,
          },
        }
      );

      if (error) throw error;

      if (data.valid) {
        trackReferralEvent({
          event: ReferralEvents.CODE_VALIDATED,
          userId: user.id,
          referralCode: referralCodeInput.trim(),
        });

        toast({
          title: "Succes",
          description: "Codul de recomandare a fost aplicat cu succes!",
        });

        setReferralCodeInput("");
        setCanEnterReferralCode(false);
        await fetchCredits();
      } else {
        trackReferralEvent({
          event: ReferralEvents.CODE_REJECTED,
          userId: user.id,
          referralCode: referralCodeInput.trim(),
          metadata: { reason: data.reason },
        });

        toast({
          title: "Eroare",
          description: data.reason || "Cod de recomandare invalid",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error submitting referral code:", error);
      toast({
        title: "Eroare",
        description: "Nu s-a putut verifica codul de recomandare",
        variant: "destructive",
      });
    } finally {
      setSubmittingReferral(false);
    }
  };

  const copyReferralLink = async () => {
    if (!referralCode) return;

    const link = createReferralLink(referralCode);

    try {
      await navigator.clipboard.writeText(link);

      trackReferralEvent({
        event: ReferralEvents.LINK_COPIED,
        userId: user?.id,
        referralCode: referralCode,
      });

      toast({
        title: "Copiat",
        description: "Link-ul de recomandare a fost copiat în clipboard!",
      });
    } catch (error) {
      console.error("Error copying to clipboard:", error);
      toast({
        title: "Eroare",
        description: "Nu s-a putut copia link-ul",
        variant: "destructive",
      });
    }
  };

  const shareOnLinkedIn = () => {
    if (!referralCode) return;

    const link = createReferralLink(referralCode);
    const text = createLinkedInShareText(link);
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}&summary=${encodeURIComponent(text)}`;

    trackReferralEvent({
      event: ReferralEvents.SHARED_LINKEDIN,
      userId: user?.id,
      referralCode: referralCode,
    });

    window.open(linkedInUrl, "_blank", "width=600,height=400");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!isEnabled) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Referral Code Input (for new users) */}
      {canEnterReferralCode && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-primary" />
              <CardTitle>Ai fost recomandat?</CardTitle>
            </div>
            <CardDescription>
              Dacă ai fost invitat de un coleg, introdu codul de recomandare
              pentru a primi credite bonus.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Introdu codul de recomandare"
                value={referralCodeInput}
                onChange={(e) => setReferralCodeInput(e.target.value)}
                className="flex-1"
              />
              <Button
                onClick={submitReferralCode}
                disabled={!referralCodeInput.trim() || submittingReferral}
              >
                {submittingReferral && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Aplică
              </Button>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <AlertCircle className="h-4 w-4" />
              Disponibil doar pentru conturi noi (primele 24 ore)
            </div>
          </CardContent>
        </Card>
      )}

      {/* Credits Dashboard */}
      {credits && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-primary" />
              <CardTitle>Creditele tale</CardTitle>
            </div>
            <CardDescription>
              Credite câștigate prin programul de recomandări
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">
                  {credits.total_credits}
                </div>
                <div className="text-sm text-muted-foreground">
                  Total credite
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">
                  {credits.pending_credits}
                </div>
                <div className="text-sm text-muted-foreground">
                  În așteptare
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{credits.used_credits}</div>
                <div className="text-sm text-muted-foreground">Utilizate</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {credits.available_discount}%
                </div>
                <div className="text-sm text-muted-foreground">
                  Reducere disponibilă
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Share Referral Code */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-primary" />
            <CardTitle>Recomandă și câștigă</CardTitle>
          </div>
          <CardDescription>
            Câștigă credite pentru fiecare coleg care completează un raport
            folosind codul tău de recomandare
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {referralCode ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                <code className="flex-1 text-sm font-mono">{referralCode}</code>
                <Button size="sm" variant="outline" onClick={copyReferralLink}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={copyReferralLink}
                  variant="outline"
                  className="flex-1"
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copiază link
                </Button>
                <Button onClick={shareOnLinkedIn} className="flex-1">
                  <Share2 className="mr-2 h-4 w-4" />
                  Distribuie pe LinkedIn
                </Button>
              </div>
            </div>
          ) : (
            <Button onClick={generateReferralCode} className="w-full">
              <Gift className="mr-2 h-4 w-4" />
              Generează codul de recomandare
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Referral History */}
      {referralHistory.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              <CardTitle>Istoricul recomandărilor</CardTitle>
            </div>
            <CardDescription>
              Persoanele pe care le-ai recomandat
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {referralHistory.map((referral) => (
                <div
                  key={referral.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="text-sm font-mono">
                      {maskEmail(referral.referred_user_id)}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {new Date(referral.used_at).toLocaleDateString("ro-RO")}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Badge
                      variant={
                        referral.report_completed ? "default" : "secondary"
                      }
                    >
                      {referral.report_completed
                        ? "Raport completat"
                        : "În așteptare"}
                    </Badge>
                    {referral.credit_awarded && (
                      <Badge variant="secondary">Credit acordat</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
